import { useState, useEffect, useRef, useMemo } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Sidebar } from "../Sidebar/Sidebar";
import { useAuth } from "../../contexts/AuthContext";
import { Menu, LogOut, User, ChevronLeft, ArrowUp } from "lucide-react";
import { type AppUser } from "../../data/menu";
import dayjs from "dayjs";

// 定数・マスタ・クエリのインポート
import { PrefTypeConst, ListingCategoryConst } from "../../constants";
import { useBiddingItemStatusCounts } from "../../hooks/useBiddingItemStatusUpdateQuery";
import { useGetBiddingDateByPrefectureForApplyQuery } from "../../hooks/useCalendarQuery";
import { useMasters } from "../../contexts/MastersContext";
import { UserRole } from "../../types/UserRole";
import { type BiddingItemStatusUpdateRequest } from "../../types/BiddingItemStatusUpdateRequest";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";

/**
 * 検索条件の型定義 (既存の型を拡張して prefectureType 等を許容する)
 */
type ExtendedStatusRequest = BiddingItemStatusUpdateRequest & {
  listingCategory?: number;
  prefectureType?: number;
};

/**
 * ============================================================================
 * CommonLayout コンポーネント
 * ============================================================================
 */
export const CommonLayout = () => {
  // --- Hooks & Context ------------------------------------------------------
  const queryClient = useQueryClient();
  const { currentUser, logout } = useAuth();
  const { sellers, isMastersLoading } = useMasters();
  const location = useLocation();
  const navigate = useNavigate();

  const mutatingCount = useIsMutating(); 

  // 更新処理が終わった（mutatingCount が 1以上から 0 になった）瞬間に数字をリロードする
  useEffect(() => {
    if (mutatingCount === 0) {
      // ステータス件数のキャッシュを無効化して再取得を走らせる
      queryClient.invalidateQueries({ 
        queryKey: ['biddingitemsstatusupdate', 'all'] 
      });
    }
  }, [mutatingCount, queryClient]);

  // サイドバー・ヘッダーの状態管理
  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth >= 1024);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const lastScrollY = useRef(0);
  const [showTopButton, setShowTopButton] = useState(false);

  // --- 1. 権限判定 ---
  const isAdmin = currentUser?.role === UserRole.Admin;
  const isSeller = currentUser?.role === UserRole.Seller;
  const isFarmer = currentUser?.role === UserRole.Farmer;
  const isBuyer = currentUser?.role === UserRole.Buyer;

  // --- 2. 自身の所属区分(県内/県外)を特定するロジック ---
  const userPrefType = useMemo(() => {
    if (isAdmin || isBuyer || isMastersLoading || !currentUser) return null;

    let defaultPref: number = PrefTypeConst.INTERNAL;
    const targetSellerCode = currentUser.parentCode;

    if ((isSeller || isFarmer) && targetSellerCode) {
      const myData = sellers.find(s => String(s.sellerCode) === String(targetSellerCode));
      if (myData) {
        defaultPref = myData.prefectureType;
      }
    }
    return defaultPref;
  }, [currentUser, sellers, isMastersLoading, isAdmin, isSeller, isFarmer, isBuyer]);

  // --- 3. データ取得ロジック ----------------------------------------------------
  
  // 次回入札日の取得 (県内・県外)
  const { data: internalDate } = useGetBiddingDateByPrefectureForApplyQuery(PrefTypeConst.INTERNAL, currentUser?.loginId);
  const { data: externalDate } = useGetBiddingDateByPrefectureForApplyQuery(PrefTypeConst.EXTERNAL, currentUser?.loginId);

  // 権限に基づいたベースリクエスト (Adminは全体、売手・生産者は自身のコード)
  const requestBase = useMemo(() => {
    return {
      sellerCode: isAdmin ? 0 : (currentUser?.parentCode || 0),
      farmerCode: isFarmer ? (currentUser?.childCode || 0) : 0,
      listingCategory: ListingCategoryConst.BIDDING,
    };
  }, [currentUser, isAdmin, isFarmer]);

  // 県内分の集計
  const internalFilters = useMemo<ExtendedStatusRequest>(() => ({
    ...requestBase,
    biddingDate: internalDate ? Number(internalDate) : 0,
    prefectureType: PrefTypeConst.INTERNAL,
  }), [requestBase, internalDate]);

  // 「買手なら空文字、そうでなければログインID」
  const { data: internalItems } = useBiddingItemStatusCounts(
    internalFilters, 
    isBuyer ? "" : (currentUser?.loginId ?? "")
  );

  // 県外分の集計
  const externalFilters = useMemo<ExtendedStatusRequest>(() => ({
    ...requestBase,
    biddingDate: externalDate ? Number(externalDate) : 0,
    prefectureType: PrefTypeConst.EXTERNAL,
  }), [requestBase, externalDate]);

  // 「買手なら空文字、そうでなければログインID」
  const { data: externalItems } = useBiddingItemStatusCounts(
    externalFilters, 
    isBuyer ? "" : (currentUser?.loginId ?? "")
  );

  // 表示制御フラグ：買手、かつ管理者は両方/売手・生産者は自区分のみ
  const showInternal = isBuyer || isAdmin || userPrefType === PrefTypeConst.INTERNAL;
  const showExternal = isBuyer || isAdmin || userPrefType === PrefTypeConst.EXTERNAL;

  const fmtDate = (d?: string | number) => d ? dayjs(String(d)).format("M/D") : "--/--";

  // --- Effects ---
  useEffect(() => {
    if (window.innerWidth < 1024) setIsSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.innerWidth >= 1024) { setIsHeaderVisible(true); return; }
      const currentScrollY = window.scrollY;
      if (isSidebarOpen || currentScrollY <= 0) { setIsHeaderVisible(true); lastScrollY.current = currentScrollY; return; }
      if (currentScrollY > lastScrollY.current && currentScrollY > 60) {
        setIsHeaderVisible(false);
      } else {
        setIsHeaderVisible(true);
      }
      lastScrollY.current = currentScrollY;
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isSidebarOpen]);

  useEffect(() => {
    const handleTopButtonScroll = () => { setShowTopButton(window.scrollY > 150); };
    window.addEventListener("scroll", handleTopButtonScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleTopButtonScroll);
  }, []);

  if (!currentUser) return null;

  const convertedUser: AppUser = {
    Id: (currentUser as any).Id || (currentUser as any).id || "unknown",
    UserName: (currentUser as any).UserName || (currentUser as any).name || "ユーザー",
    role: (currentUser as any).role,
    permission: (currentUser as any).permission,
  };

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col overflow-x-hidden">
      {/* =======================================================================
        ヘッダー (Header)
      ======================================================================= */}
      <header
        className={`
          fixed top-0 left-0 w-full h-14 bg-slate-800 shadow-md z-50 flex items-center px-0
          transition-transform duration-300 ease-in-out
          ${isHeaderVisible ? "translate-y-0" : "-translate-y-full"}
          lg:translate-y-0
        `}
      >
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="w-14 h-14 flex items-center justify-center bg-transparent border-none text-slate-300 hover:text-white hover:bg-slate-700 transition-colors focus:outline-none flex-shrink-0"
        >
          {isSidebarOpen ? <ChevronLeft size={24} /> : <Menu size={24} />}
        </button>

        <div className="flex-1 min-w-0 px-2 cursor-pointer group" onClick={() => navigate("/")}>
          <h1 className="text-white font-bold text-sm md:text-lg tracking-tight truncate group-hover:text-slate-200 transition-colors">
            静岡茶市場電子入札システム
          </h1>
        </div>

        {/* PC用予定表示 */}
        <div className="hidden lg:flex items-center gap-4 px-4 text-sm text-slate-300 border-l border-slate-700 h-10 leading-tight">
          {showInternal && (
            <div>
              <p className="text-blue-400 font-bold">次回県内入札日：{fmtDate(internalDate)}</p>
              {!isBuyer && (
                <p>未提出: {internalItems?.INPUT ?? 0}件 / 提出済: {internalItems?.APPLIED ?? 0}件 / 確定済: {internalItems?.CONFIRMED ?? 0}件</p>
              )}
            </div>
          )}
          {(isAdmin || isBuyer) && showInternal && showExternal && (
            <div className="w-px h-6 bg-slate-700 mx-1"></div>
          )}
          {showExternal && (
            <div>
              <p className="text-orange-400 font-bold">次回県外入札日：{fmtDate(externalDate)}</p>
              {!isBuyer && (
                <p>未提出: {externalItems?.INPUT ?? 0}件 / 提出済: {externalItems?.APPLIED ?? 0}件 / 確定済: {externalItems?.CONFIRMED ?? 0}件</p>
              )}
            </div>
          )}
        </div>
      </header>

      {/* =======================================================================
        スマホ用：常に表示される状況パネル 
      ======================================================================= */}
      <div className={`
        lg:hidden fixed top-14 left-0 w-full bg-slate-800 border-t border-slate-700 text-white z-30 px-5 py-3 shadow-xl
        transition-transform duration-300 ease-in-out
        ${isHeaderVisible ? "translate-y-0" : "-translate-y-[200px]"}
      `}>
        {/* 買手(isBuyer)なら横並び(flex-row)、それ以外は縦並び(space-y-3) */}
        <div className={isBuyer ? "flex flex-row items-center justify-around gap-2" : "space-y-3"}>

          {/* --- 県内入札セクション --- */}
          {showInternal && (
            <div className={isBuyer ? "text-[12px] whitespace-nowrap" : "flex justify-between items-center text-[13px]"}>
              <span className="font-bold text-blue-400">次回県内入札日 ({fmtDate(internalDate)})</span>
              {!isBuyer && (
                <div className="flex gap-2 text-slate-300 text-[11px]">
                  <span>未提出: <b className="text-white font-bold">{internalItems?.INPUT ?? 0}件</b></span>
                  <span>提出済: <b className="text-white font-bold">{internalItems?.APPLIED ?? 0}件</b></span>
                  <span>確定済: <b className="text-white font-bold">{internalItems?.CONFIRMED ?? 0}件</b></span>
                </div>
              )}
            </div>
          )}

          {/* 区切り線：買手なら縦線、Adminなら横線を表示 */}
          {(isAdmin || isBuyer) && showInternal && showExternal && (
            <div className={isBuyer ? "w-px h-4 bg-slate-700 mx-1" : "h-[1px] bg-slate-700"}></div>
          )}

          {/* --- 県外入札セクション --- */}
          {showExternal && (
            <div className={isBuyer ? "text-[12px] whitespace-nowrap" : "flex justify-between items-center text-[13px]"}>
              <span className="font-bold text-orange-400">次回県外入札日 ({fmtDate(externalDate)})</span>
              {!isBuyer && (
                <div className="flex gap-2 text-slate-300 text-[11px]">
                  <span>未提出: <b className="text-white font-bold">{externalItems?.INPUT ?? 0}件</b></span>
                  <span>提出済: <b className="text-white font-bold">{externalItems?.APPLIED ?? 0}件</b></span>
                  <span>確定済: <b className="text-white font-bold">{externalItems?.CONFIRMED ?? 0}件</b></span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* スマホ用オーバーレイ */}
      {isSidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setIsSidebarOpen(false)} />
      )}

      {/* サイドバー */}
      <aside
        className={`
          fixed top-14 left-0 bottom-0 z-40 bg-slate-800 text-white flex flex-col overflow-hidden transition-all duration-300
          w-64 ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"}
          lg:shadow-none lg:translate-x-0 ${isSidebarOpen ? "lg:w-64" : "lg:w-0"}
        `}
      >
        <div className="flex-1 overflow-y-auto py-4 min-w-[16rem]">
          <Sidebar currentUser={convertedUser} />
        </div>
        <div className="p-4 border-t border-slate-700 bg-slate-900/30 min-w-[16rem]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-slate-600 flex items-center justify-center text-white"><User size={16} /></div>
            <div className="overflow-hidden">
              <div className="text-sm font-medium truncate text-white">{convertedUser.UserName}</div>
              <div className="text-xs text-slate-300 truncate capitalize">{convertedUser.role}</div>
            </div>
          </div>
          <button onClick={() => logout && logout()} className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded border border-slate-600 bg-transparent text-xs text-slate-100 hover:bg-slate-700 transition-colors">
            <LogOut size={14} /> ログアウト
          </button>
        </div>
      </aside>

      {/* メインコンテンツ */}
      <main
        className={`
          transition-all duration-300 min-h-screen flex-1
          ${isSidebarOpen ? "lg:ml-64" : "lg:ml-0"}
          ${isBuyer ? "pt-24" : (isAdmin ? "pt-36" : "pt-28")} 
          lg:pt-14
        `}
      >
        <div className="p-1.5 md:p-6 w-full">
          <Outlet />
        </div>
      </main>

      {/* 戻るボタン */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        className={`
          fixed bottom-6 right-6 z-50 p-3 bg-slate-600/80 hover:bg-slate-600 text-white rounded-full shadow-lg transition-all
          ${showTopButton ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10 pointer-events-none"}
        `}
      >
        <ArrowUp size={24} />
      </button>
    </div>
  );
};